import * as React from 'react'

import { useApolloClient } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { useFilters } from '../CommunityFilters/useFilters'
import {
  clearPendingEngagementIds,
  requestNewEngagements,
  useHasPendingEngagements,
} from '../hooks/engagement/newEngagementSignal'
import { useNewMentionsAvailableButton } from '../hooks/backfill/useNewMentionsAvailableButton'
import { NewItemsAvailableButton } from '../NewItemsAvailableButton'

import type { JSX } from 'react'

type NewMentionsAvailableButtonProps = {
  /**
   * Present in the single-channel view to scope the pill to that channel's
   * mentions. Omitted in the all-channels view, where every channel counts.
   */
  channelId?: string
}

export const NewMentionsAvailableButton = ({
  channelId,
}: NewMentionsAvailableButtonProps): JSX.Element => {
  const [isFetchingNewMentions, setIsFetchingNewMentions] =
    React.useState(false)
  const client = useApolloClient()

  const {
    filters: { status, sortOption },
  } = useFilters()

  const { showNewMentionsButton, hideNewMentionsButton } =
    useNewMentionsAvailableButton({ channelId })

  const handleFetchNewMentions = async (): Promise<void> => {
    setIsFetchingNewMentions(true)
    // Tell every mounted list a jump was asked for, so it scrolls toward where
    // the new mentions will appear and can spot them once they land.
    requestNewEngagements()
    try {
      // The mentions inbox is a single active query with no by-post variant, so
      // a name-scoped refetch reloads it with the current filters in place.
      await client.refetchQueries({ include: ['MentionInbox'] })
      // Every sort but oldest-first lands new mentions on the first page, which
      // the refetch above reloaded. An id still pending after that can never
      // show up: a filter hides it, or no list rendered it. It has to be
      // dropped, or it keeps the pill on screen with nothing left to reveal.
      if (sortOption !== 'oldest') {
        clearPendingEngagementIds('mention')
      }
    } catch {
      // Leaving the ingested ids pending keeps the pill on screen, which is the
      // only way back to mentions that never arrived.
      toast.error('Error fetching new mentions')
    } finally {
      setIsFetchingNewMentions(false)
      hideNewMentionsButton()
    }
  }

  // A newly ingested mention is unanswered, so it only belongs in views whose
  // status filter would include it. `new` and `all` map to "no status filter".
  const isValidFilter =
    status === 'all' || status === 'unreplied' || status === 'new'

  // Under oldest-first the new mentions sort onto the last page, so they can be
  // several pages below. Keep the pill up until the user has actually reached
  // them, giving them something to click to keep moving down the list.
  const hasUnreachedMentions = useHasPendingEngagements('mention')

  const shouldShow =
    isValidFilter &&
    (isFetchingNewMentions || showNewMentionsButton || hasUnreachedMentions)

  return (
    <NewItemsAvailableButton
      show={shouldShow}
      isFetching={isFetchingNewMentions}
      label="New mentions available"
      onClick={handleFetchNewMentions}
      arrowDown={sortOption === 'oldest'}
    />
  )
}
